/**
 * StorageService - localStorage abstraction layer
 * Handles persistence of player data, settings, and game history
 * Based on ADR-003: Game State Management Architecture
 */

import type { PlayerState, GameSettings } from '@/types';
import { STORAGE_KEYS, GAME_CONSTANTS } from '@/types';

/**
 * StorageService
 * Provides safe localStorage operations with error handling
 */
export class StorageService {
  /**
   * Save player state to localStorage
   */
  public savePlayerState(playerState: PlayerState): void {
    try {
      // Limit game history size
      const limitedState = {
        ...playerState,
        gameHistory: playerState.gameHistory.slice(0, GAME_CONSTANTS.MAX_HISTORY_ENTRIES),
      };
      
      localStorage.setItem(STORAGE_KEYS.PLAYERS, JSON.stringify(limitedState));
    } catch (error) {
      console.error('Failed to save player state:', error);
    }
  }

  /**
   * Load player state from localStorage
   */
  public loadPlayerState(): PlayerState | null {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.PLAYERS);
      if (!data) {
        return null;
      }

      const parsed = JSON.parse(data) as PlayerState;
      
      // Convert date strings back to Date objects
      if (parsed.gameHistory) {
        parsed.gameHistory = parsed.gameHistory.map((result) => ({
          ...result,
          completedAt: new Date(result.completedAt),
          moveHistory: result.moveHistory.map((move) => ({
            ...move,
            timestamp: new Date(move.timestamp),
          })),
        }));
      }

      return parsed;
    } catch (error) {
      console.error('Failed to load player state:', error);
      return null;
    }
  }

  /**
   * Save game settings to localStorage
   */
  public saveSettings(settings: GameSettings): void {
    try {
      localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  }

  /**
   * Load game settings from localStorage
   */
  public loadSettings(): GameSettings | null {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.SETTINGS);
      if (!data) {
        return null;
      }

      return JSON.parse(data) as GameSettings;
    } catch (error) {
      console.error('Failed to load settings:', error);
      return null;
    }
  }

  /**
   * Clear all stored data
   */
  public clearAll(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.PLAYERS);
      localStorage.removeItem(STORAGE_KEYS.SETTINGS);
      localStorage.removeItem(STORAGE_KEYS.HISTORY);
    } catch (error) {
      console.error('Failed to clear storage:', error);
    }
  }
}
